import { AngularReactDialog, IOmniaShell, handleApiError, react } from "../../omnia";
import { IFieldsService } from "../../types";

import classes = react.classes;
import Msg = react.Msg;
import Checkbox = react.Checkbox;
import { SetupCropYearDialog } from "@omnia/shared/fields/cropYear/SetupCropYearDialog";
import { EditContext } from "@omnia/shared/fields/cropYear/editContext";
import { TargetCategory } from "@omnia/shared/fields/cropYear/targetCategory";
import { TargetField } from "@omnia/shared/fields/cropYear/targetField";
const Crops = SetupCropYearDialog.Crops;

enum DialogStep {
    S1_Cropping = 1,
    S2_Confirm = 2,
}

export const ReactOpenCropYearDialog: AngularReactDialog = {

    inject: {
        shell: "OmniaShell",
        fieldSvc: "OmniaFieldsService",
    },

    component: OpenCropYearDialog

}

/**
 * Dialog to setup a cropping year & create new uncropped areas for fields with no cropping information.
 * @param dependencies: the dependencies injected by Angular
 * @param close: the function to close the dialog
 * @param state: the initial state
 * @returns  the React component
 */
function OpenCropYearDialog({ dependencies, close }: react.ReactAngularComponentContext) {
    const shell: IOmniaShell = dependencies["shell"];
    const fieldSvc: IFieldsService = dependencies["fieldSvc"];

    const [{ targetYear, step, fieldsEditContext, session }, setState] = React.useState(() => {
        const session = shell.getSession();
        const targetYear = fieldSvc.yearId + 1;
        const fieldsEditContext = new EditContext(targetYear, fieldSvc, false, false);
        const step = DialogStep.S1_Cropping;

        return {
            session,
            targetYear,
            fieldsEditContext,
            step,
        };
    });

    const forceRefreshState = React.useCallback(() => setState((prev) => ({ ...prev })), []);

    const categories = TargetCategory.create(fieldsEditContext.targets);

    const hasSelectedCrops = fieldsEditContext.hasSelectedCrops();

    const goToPreviousStep = React.useCallback(() => setState((prev) => ({ ...prev, step: prev.step - 1 })), []);
    const goToNextStep = React.useCallback(() => setState((prev) => ({ ...prev, step: prev.step + 1 })), []);

    const [isCommitting, setIsCommitting] = React.useState(false);

    const canCommit = !isCommitting && fieldsEditContext.canCommit();

    const commit = React.useCallback(() => {
        if (!canCommit) return;
        setIsCommitting(true);

        const model = fieldsEditContext.getCroppingYearModel(true);

        session
            .openCroppingYear(model)
            .then((ok) => ok && close())
            .catch((r) =>
                handleApiError(shell, r, {
                    defaultMessage: "An error has occurred while opening the new cropping year",
                })
            )
            .finally(() => {
                setIsCommitting(false);
            });
    }, [canCommit, fieldsEditContext, session, targetYear, shell]);

    // Whenever the step changes, scroll the modal contents back to the top.
    const stepContents = React.useRef<HTMLDivElement>();
    React.useLayoutEffect(() => {
        const el = stepContents?.current;
        if (el) {
            el.scrollTop = 0;
        }
    }, [step]);

    return (
        <div className="omnia-open-crop-year-dialog">
            <omnia.react.DialogHeader icon="calendar">
                <Msg text="Open cropping year {{targetYear}}" args={{ targetYear }} />
            </omnia.react.DialogHeader>
            <omnia.react.DialogBody>
                <div className="step-info">
                    <div>
                        <p>
                            {step === 1 ? (
                                <Msg
                                    text="Step 1/2 - Please select the fields to carry over to year {{targetYear}} from the list below."
                                    args={{ targetYear }}
                                />
                            ) : step === 2 ? (
                                <Msg text="Step 2/2 - Review and confirm" />
                            ) : null}
                        </p>
                    </div>
                </div>
                {step === 2 && !hasSelectedCrops ? (
                    <div className="alert alert-info no-selected-crops-warning">
                        <p>
                            <Msg text="No new crops will be created on year {{targetYear}}" args={{ targetYear }} />
                        </p>
                    </div>
                ) : null}
                <div ref={stepContents} className="step-contents">
                    <Crops categories={categories} readOnly={step === 2} onChange={forceRefreshState} />
                    <Fields
                        fields={fieldsEditContext.fields}
                        targetYear={targetYear}
                        readOnly={step === 2}
                        onChange={forceRefreshState}
                    />
                </div>
            </omnia.react.DialogBody>
            <omnia.react.DialogFooter>
                <omnia.react.CancelButton onClick={close} />
                <omnia.react.DefaultButton disabled={step <= 1} onClick={goToPreviousStep}>
                    <Msg>Previous</Msg>
                </omnia.react.DefaultButton>
                {step < 2 ? (
                    <omnia.react.CloseButton disabled={!canCommit} onClick={goToNextStep}>
                        <Msg>Next</Msg>
                    </omnia.react.CloseButton>
                ) : (
                    <omnia.react.SaveButton disabled={!canCommit} onClick={commit}>
                        <Msg>Open New Year</Msg>
                    </omnia.react.SaveButton>
                )}
            </omnia.react.DialogFooter>
        </div>
    );
}

type FieldsProps = {
    fields: TargetField[];
    targetYear: number;
    readOnly?: boolean;
    onChange?: () => void;
};

const Fields = ({ targetYear, fields, readOnly, onChange }: FieldsProps) => {
    const disabledFields = fields.filter((x) => x.disabled);
    const hasDisabledFields = !!disabledFields.length;
    const [open, setOpen] = React.useState(hasDisabledFields);
    const isOpen = open || hasDisabledFields || readOnly;

    const toggleSelection = React.useCallback(
        (fields: TargetField[]) => {
            const nextValue = fields.some((x) => !x.disabled);
            fields.forEach((x) => (x.disabled = nextValue));
            onChange();
        },
        [onChange]
    );

    return (
        <section>
            <div className="container-fluid">
                <div className="row">
                    <div className="col col-md-12">
                        <h4>
                            <Msg>Disabled fields</Msg>
                            {!readOnly ? (
                                <button
                                    type="button"
                                    className="btn btn-link btn-icon-toggle btn-expand"
                                    onClick={() => setOpen((prev) => !prev)}
                                    disabled={hasDisabledFields}
                                >
                                    <i className={classes("icon", isOpen ? "icon-down" : "icon-up")} />
                                </button>
                            ) : null}
                        </h4>
                        {isOpen ? (
                            <div>
                                {!readOnly ? (
                                    <p>
                                        Click&nbsp;
                                        <omnia.react.Popup
                                            popupCssClass="omnia-open-crop-year-disable-fields-popover"
                                            popupContent={() => (
                                                <ul>
                                                    {fields.map((f) => (
                                                        <li key={f.fieldId}>
                                                            <Checkbox
                                                                cssClass={classes({ selected: f.disabled })}
                                                                checked={f.disabled}
                                                                setChecked={() => toggleSelection([f])}
                                                            >
                                                                {f.description}
                                                            </Checkbox>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        >
                                            <button type="button" className="btn btn-floating-action small white">
                                                <i className="fas fa-pen" />
                                            </button>
                                        </omnia.react.Popup>
                                        &nbsp;here to select fields to disable.
                                    </p>
                                ) : null}
                                {hasDisabledFields ? (
                                    <>
                                        <p>
                                            <Msg
                                                text="The following fields will be disabled on year {{targetYear}}:"
                                                args={{ targetYear }}
                                            />
                                        </p>
                                        <ul className="disabled-fields">
                                            {disabledFields.map((x) => (
                                                <li className="field">{x.description}</li>
                                            ))}
                                        </ul>
                                    </>
                                ) : readOnly ? (
                                    <p>
                                        <Msg
                                            text="There are no disabled fields for year {{targetYear}}"
                                            args={{ targetYear }}
                                        />
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
};
